import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Loader from '../../components/common/Loader';
import { getCompanyJobs, deleteJob } from '../../api/company';
import { useToast } from '../../context/ToastContext';

const CompanyJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const res = await getCompanyJobs();
      setJobs(res.data || []);
    } catch (err) {
      addToast('Failed to load jobs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) return;
    
    try {
      await deleteJob(id);
      addToast('Job deleted successfully', 'success');
      setJobs(jobs.filter(j => j.id !== id));
    } catch (err) {
      addToast('Failed to delete job', 'error');
    }
  };

  return (
    <div className="page-wrapper dashboard-page">
      <Navbar />
      
      <main className="container pt-8 pb-20 flex-1">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="heading-1">My Jobs</h1>
            <p className="subheading">Manage your job postings and internships.</p>
          </div>
          <Button variant="primary" onClick={() => navigate('/company/jobs/new')}>
            + Post New Job
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader /></div>
        ) : jobs.length === 0 ? (
          <div className="card-elevated empty-state">
            <div className="empty-icon">🏢</div>
            <h3 className="empty-title">No jobs posted yet</h3>
            <p className="empty-desc">Create your first job posting to start receiving applications from talented students.</p>
            <Button variant="primary" className="mt-4" onClick={() => navigate('/company/jobs/new')}>
              Post a Job Now
            </Button>
          </div>
        ) : (
          <div className="grid grid-2 gap-6">
            {jobs.map(job => (
              <div key={job.id} className="job-card">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="heading-3 mb-1">{job.title}</h3>
                    <div className="flex items-center gap-2">
                      <Badge variant="primary-light">{job.employment_type}</Badge>
                      <Badge variant={job.status === 'closed' ? 'gray' : 'success'}>
                        {job.status === 'closed' ? 'Closed' : 'Active'}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600 my-4 line-clamp-2">
                  {job.description}
                </div>
                
                <div className="job-meta">
                  <div className="job-meta-item">📍 {job.location}</div>
                  {job.openings && <div className="job-meta-item">👥 {job.openings} openings</div>}
                  <div className="job-meta-item">📅 Deadline: {new Date(job.deadline).toLocaleDateString()}</div>
                </div>
                
                <div className="border-t border-gray-100 mt-4 pt-4 flex justify-between items-center">
                  <div className="text-sm font-semi text-gray-700">
                    {/* The API doesn't return applicants count per job directly here, would need to calculate if backend provided it. Just a placeholder for now */}
                    <span className="text-primary mr-1">Manage</span> Applicants
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/company/jobs/edit/${job.id}`)}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" className="text-danger hover:text-white hover:bg-danger" onClick={() => handleDelete(job.id)}>
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default CompanyJobs;